import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { addDoc, collection } from 'firebase/firestore';
import { storage, firestore } from '../../database/firebase';
import { Upload, X, Image, Video, Loader2 } from 'lucide-react';

export default function MediaUpload({ organizationId, onUploadComplete, onClose }) {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [mediaType, setMediaType] = useState('photo');
  const [caption, setCaption] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const fileInputRef = useRef(null);

  const handleFileSelect = event => {
    const files = Array.from(event.target.files);
    const validFiles = files.filter(file => {
      if (mediaType === 'photo') {
        return file.type.startsWith('image/');
      } else {
        return file.type.startsWith('video/');
      }
    });
    setSelectedFiles(validFiles);
  };

  const removeFile = index => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const uploadMedia = async () => {
    if (selectedFiles.length === 0) return;

    setUploading(true);
    setUploadProgress(0);

    try {
      const uploadPromises = selectedFiles.map(async (file, index) => {
        const timestamp = Date.now();
        const fileName = `${organizationId}/${mediaType}s/${timestamp}_${file.name}`;
        const storageRef = ref(storage, fileName);

        const snapshot = await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(snapshot.ref);

        setUploadProgress(((index + 1) / selectedFiles.length) * 100);

        const mediaData = {
          url: downloadURL,
          caption: caption || file.name,
          type: mediaType,
          fileName: file.name,
          fileSize: file.size,
          uploadedAt: new Date(),
          organizationId: organizationId,
        };

        const docRef = await addDoc(
          collection(firestore, 'companyProfiles', organizationId, 'media'),
          mediaData
        );

        return {
          id: docRef.id,
          ...mediaData,
        };
      });

      const uploadedMedia = await Promise.all(uploadPromises);

      onUploadComplete(uploadedMedia);

      setSelectedFiles([]);
      setCaption('');
      setUploadProgress(0);

      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (error) {
      alert('Error uploading media. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const formatFileSize = bytes => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        className="w-[92%] max-w-xl rounded-2xl bg-white p-4 shadow-2xl sm:w-full md:max-w-2xl md:p-6"
      >
        <div className="mb-6 flex items-center justify-between">
          <h2 className="text-2xl font-semibold text-slate-900">Upload Media</h2>
          <button onClick={onClose} className="rounded-full p-2 hover:bg-slate-100">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="mb-6">
          <label className="mb-2 block text-sm font-medium text-slate-700">Media Type</label>
          <div className="flex space-x-4">
            <button
              onClick={() => setMediaType('photo')}
              className={`flex items-center space-x-2 rounded-lg px-4 py-2 transition-all ${
                mediaType === 'photo'
                  ? 'bg-blue-100 text-blue-700'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              <Image className="h-4 w-4" />
              <span>Photos</span>
            </button>
            <button
              onClick={() => setMediaType('video')}
              className={`flex items-center space-x-2 rounded-lg px-4 py-2 transition-all ${
                mediaType === 'video'
                  ? 'bg-blue-100 text-blue-700'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              <Video className="h-4 w-4" />
              <span>Videos</span>
            </button>
          </div>
        </div>

        <div className="mb-6">
          <label className="mb-2 block text-sm font-medium text-slate-700">
            Select {mediaType === 'photo' ? 'Photos' : 'Videos'}
          </label>
          <div
            onClick={() => fileInputRef.current?.click()}
            className="cursor-pointer rounded-xl border-2 border-dashed border-slate-300 p-8 text-center hover:border-slate-400"
          >
            <Upload className="mx-auto mb-4 h-12 w-12 text-slate-400" />
            <p className="text-lg font-medium text-slate-700">
              Click to select {mediaType === 'photo' ? 'photos' : 'videos'}
            </p>
            <p className="text-sm text-slate-500">
              {mediaType === 'photo'
                ? 'Supports: JPG, PNG, GIF, WebP'
                : 'Supports: MP4, MOV, AVI, WebM'}
            </p>
            <p className="mt-2 text-xs text-slate-400">
              Max file size: {mediaType === 'photo' ? '10MB' : '100MB'}
            </p>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={mediaType === 'photo' ? 'image/*' : 'video/*'}
            onChange={handleFileSelect}
            className="hidden"
          />
        </div>

        {selectedFiles.length > 0 && (
          <div className="mb-6">
            <label className="mb-2 block text-sm font-medium text-slate-700">
              Selected Files ({selectedFiles.length})
            </label>
            <div className="space-y-2">
              {selectedFiles.map((file, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between rounded-lg border border-slate-200 p-3"
                >
                  <div className="flex items-center space-x-3">
                    {mediaType === 'photo' ? (
                      <Image className="h-5 w-5 text-slate-400" />
                    ) : (
                      <Video className="h-5 w-5 text-slate-400" />
                    )}
                    <div>
                      <p className="font-medium text-slate-900">{file.name}</p>
                      <p className="text-sm text-slate-500">{formatFileSize(file.size)}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => removeFile(index)}
                    className="rounded-full p-1 hover:bg-slate-100"
                  >
                    <X className="h-4 w-4 text-slate-400" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mb-6">
          <label className="mb-2 block text-sm font-medium text-slate-700">
            Caption (optional)
          </label>
          <input
            type="text"
            value={caption}
            onChange={e => setCaption(e.target.value)}
            placeholder={`Enter caption for ${mediaType === 'photo' ? 'photos' : 'videos'}`}
            className="w-full rounded-xl border border-slate-300 px-4 py-3 focus:border-transparent focus:ring-2 focus:ring-slate-600"
          />
        </div>

        {uploading && (
          <div className="mb-6">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-sm font-medium text-slate-700">Uploading...</span>
              <span className="text-sm text-slate-500">{Math.round(uploadProgress)}%</span>
            </div>
            <div className="h-2 rounded-full bg-slate-200">
              <motion.div
                className="h-full rounded-full bg-blue-600"
                initial={{ width: 0 }}
                animate={{ width: `${uploadProgress}%` }}
                transition={{ duration: 0.3 }}
              />
            </div>
          </div>
        )}
        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            disabled={uploading}
            className="rounded-xl border border-slate-300 px-6 py-2 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={uploadMedia}
            disabled={uploading || selectedFiles.length === 0}
            className="flex items-center space-x-2 rounded-xl bg-slate-900 px-6 py-2 text-white hover:opacity-90 disabled:opacity-50"
          >
            {uploading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Uploading...</span>
              </>
            ) : (
              <>
                <Upload className="h-4 w-4" />
                <span>
                  Upload {selectedFiles.length} {mediaType === 'photo' ? 'Photo(s)' : 'Video(s)'}
                </span>
              </>
            )}
          </button>
        </div>
      </motion.div>
    </div>
  );
}
